/**
 * Where a camera pick lands, and its one write. With one camera up, a pick retargets an open
 * camera surface or opens the view. On a scene, a click adds the camera, a double-click replaces
 * the scene, and a moment goes to the scene. The plan has no side effects, so a test needs no
 * shell; the shell applies it.
 */

import { addCameraCommand, focusCameraCommand, showCameraCommand } from '@/lib/cameraSeek';
import { readScope, scopeKey } from '@/lib/viewScope';
import type {
    CameraPickInput, CameraPickPlan, CameraPickWrite, CameraSelection, PanelId,
} from '@/types/panels';
import { CameraPickAction, PanelMode } from '@/types/panels';
import type { ViewScope } from '@/types/viewScope';
import { ViewScopeKind } from '@/types/viewScope';

const CAMERA_SURFACES: PanelId[] = ['camera_settings', 'camera_view'];
const VIEW_PANEL: PanelId = 'camera_view';

interface CameraPickIntent {
    shouldReplace?: boolean;
    shouldSeek?: boolean;
}

/** Where a pick lands: a panel for every action but an add, which goes to the scene. */
type CameraPickRoute =
    | {
        action: CameraPickAction.Open | CameraPickAction.Retarget | CameraPickAction.Focus;
        panelId: PanelId;
        shouldJoin?: never;
    } |
    {
        action: CameraPickAction.Add;
        panelId?: never;
        /** A plain click on a scene: the only pick that shows the hint of the double-click. */
        shouldJoin: true;
    };

/** The rule of a pick; `isMulti` tells whether the view shows several cameras. */
function routeCameraPick(
    openIds: ReadonlySet<PanelId>, isMulti: boolean, intent: CameraPickIntent,
): CameraPickRoute {
    const isScene = openIds.has(VIEW_PANEL) && isMulti;

    if (isScene && intent.shouldReplace) {
        return { action: CameraPickAction.Open, panelId: VIEW_PANEL };
    }

    if (isScene) {
        if (intent.shouldSeek) {
            return { action: CameraPickAction.Focus, panelId: VIEW_PANEL };
        }
        if (openIds.has('camera_settings')) {
            return { action: CameraPickAction.Retarget, panelId: 'camera_settings' };
        }
        return { action: CameraPickAction.Add, shouldJoin: true };
    }

    if (intent.shouldSeek) {
        return openIds.has(VIEW_PANEL)
            ? { action: CameraPickAction.Retarget, panelId: VIEW_PANEL }
            : { action: CameraPickAction.Open, panelId: VIEW_PANEL };
    }
    const target = CAMERA_SURFACES.find((id) => openIds.has(id));
    return target
        ? { action: CameraPickAction.Retarget, panelId: target }
        : { action: CameraPickAction.Open, panelId: VIEW_PANEL };
}

/** What the camera view shows now, as far as a pick reads it. */
interface ViewSummary {
    isViewOpen: boolean;
    viewProps: Record<string, unknown>;
    isInSettings: boolean;
    isMulti: boolean;
}

interface PickFlags {
    shouldSeek: boolean;
    isLeavingSettings: boolean;
    isScoped: boolean;
}

// `selection` carries the intent: the moment (`seekAtMs`, `isLive`), the `zoom` window, the
// gesture (`shouldReplace`), the cameras opened beside it (`also`) and the view's `scope`.
export function planCameraPick(input: CameraPickInput): CameraPickPlan {
    const { cameraId, selection, visibleIds, nowMs } = input;
    const view = readView(input);
    const flags = getPickFlags(selection, view);
    const momentProps = buildMomentProps(cameraId, selection, nowMs, flags.shouldSeek);
    if (flags.isScoped && selection.scope.kind !== ViewScopeKind.FullArchive) {
        return planScopedPick(cameraId, selection.scope, momentProps, flags, visibleIds);
    }
    const route = chooseRoute(input, view, flags);
    const targetPanelId = route.panelId ?? VIEW_PANEL;
    return {
        write: buildPickWrite(route, input, momentProps, flags),
        isJoinHintShown: route.shouldJoin === true,
        focusId: visibleIds.has(targetPanelId) ? null : targetPanelId,
        action: route.action,
    };
}

function readView({ openPanels, mode }: CameraPickInput): ViewSummary {
    const viewProps = openPanels.find((panel) => panel.id === VIEW_PANEL)?.props ?? {};
    const isInSettings = mode === PanelMode.Settings;
    const { scene } = viewProps;
    const sceneSize = typeof scene === 'object' && scene !== null &&
        'items' in scene && Array.isArray(scene.items) ? scene.items.length : 0;
    return {
        isViewOpen: openPanels.some((panel) => panel.id === VIEW_PANEL),
        viewProps,
        isInSettings,
        isMulti: !isInSettings && (!!viewProps.layoutId || sceneSize > 1),
    };
}

function getPickFlags(selection: CameraSelection, { isInSettings }: ViewSummary): PickFlags {
    const { seekAtMs = null, isLive = false } = selection;
    const shouldSeek = seekAtMs !== null || isLive;
    const isLeavingSettings = isInSettings && shouldSeek;
    return { shouldSeek, isLeavingSettings, isScoped: !isInSettings || isLeavingSettings };
}

// Each moment prop names its camera, so a timeline that still shows the outgoing camera does
// not take the moment.
function buildMomentProps(
    cameraId: string, selection: CameraSelection, nowMs: number, shouldSeek: boolean,
): Record<string, unknown> {
    const { seekAtMs = null, isLive = false, zoom = null } = selection;
    const momentMs = isLive ? null : seekAtMs;
    const seekProps = shouldSeek
        ? {
            t: momentMs, seekNonce: nowMs, pickCameraId: cameraId,
        }
        : {};
    const zoomProps = zoom
        ? {
            zoomStartMs: zoom.startMs, zoomEndMs: zoom.endMs, zoomNonce: nowMs,
            pickCameraId: cameraId,
        }
        : {};
    return { ...seekProps, ...zoomProps };
}

function buildOpenWrite(
    props: Record<string, unknown>, { isLeavingSettings }: PickFlags,
): CameraPickWrite {
    return isLeavingSettings
        ? { kind: 'open', props, mode: PanelMode.View }
        : { kind: 'open', props };
}

function planScopedPick(
    cameraId: string, scope: ViewScope, momentProps: Record<string, unknown>, flags: PickFlags,
    visibleIds: ReadonlySet<PanelId>,
): CameraPickPlan {
    return {
        write: buildOpenWrite({
            cameraId: scope.kind === ViewScopeKind.Bookmark ? cameraId : null,
            layoutId: null,
            command: null,
            scope,
            ...momentProps,
        }, flags),
        isJoinHintShown: false,
        focusId: visibleIds.has(VIEW_PANEL) ? null : VIEW_PANEL,
        action: CameraPickAction.Open,
    };
}

function chooseRoute(
    { openPanels, selection }: CameraPickInput, view: ViewSummary, flags: PickFlags,
): CameraPickRoute {
    const { shouldReplace = false, also = [], scope } = selection;
    const { shouldSeek, isLeavingSettings, isScoped } = flags;
    const isRescoped = isScoped && view.isViewOpen &&
        scopeKey(readScope(view.viewProps.scope)) !== scopeKey(scope);
    if (isLeavingSettings || also.length > 0 || isRescoped) {
        return { action: CameraPickAction.Open, panelId: VIEW_PANEL };
    }
    return routeCameraPick(
        new Set(openPanels.map((panel) => panel.id)), view.isMulti,
        view.isInSettings ? { shouldSeek } : { shouldReplace, shouldSeek });
}

function buildPickWrite(
    route: CameraPickRoute, { cameraId, selection }: CameraPickInput,
    momentProps: Record<string, unknown>, flags: PickFlags,
): CameraPickWrite {
    const { seekAtMs = null, also = [], scope } = selection;
    const scopeProps = flags.isScoped && route.panelId !== 'camera_settings' ? { scope } : {};
    switch (route.action) {
        case CameraPickAction.Retarget:
            return {
                kind: 'props',
                panelId: route.panelId,
                props: { cameraId, ...momentProps, ...scopeProps },
            };
        case CameraPickAction.Focus:
            return {
                kind: 'props',
                panelId: route.panelId,
                props: {
                    command: focusCameraCommand(cameraId, seekAtMs),
                    ...scopeProps,
                    ...momentProps,
                },
            };
        case CameraPickAction.Add:
            return {
                kind: 'props',
                panelId: VIEW_PANEL,
                props: { command: addCameraCommand(cameraId, null, route.shouldJoin) },
            };
        case CameraPickAction.Open:
        default:
            return buildOpenWrite({
                cameraId,
                layoutId: null,
                command: showCameraCommand(cameraId, also),
                ...scopeProps,
                ...momentProps,
            }, flags);
    }
}
